import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  api,
  parseApiError,
  type CreditPack,
  type CreditTransaction,
  type Me,
} from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { Coins, Zap, Rocket, CheckCircle2, AlertCircle, ArrowDown, ArrowUp, Gift } from 'lucide-react';
import { cn } from '../lib/cn';

const PACKS: { key: CreditPack; credits: number; priceZar: number; label: string; icon: typeof Coins }[] = [
  { key: 'STARTER_50', credits: 50, priceZar: 99, label: 'Starter', icon: Coins },
  { key: 'VALUE_200', credits: 200, priceZar: 275, label: 'Value', icon: Zap },
  { key: 'PRO_500', credits: 500, priceZar: 550, label: 'Pro', icon: Rocket },
];

function formatZar(amount: number) {
  return `R${amount.toLocaleString('en-ZA')}`;
}

function timeAgo(ts: number) {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function txIcon(type: CreditTransaction['type']) {
  switch (type) {
    case 'PURCHASE': return <ArrowUp className="h-4 w-4 text-green-400" />;
    case 'DEBIT': return <ArrowDown className="h-4 w-4 text-red-400" />;
    case 'GRANT': return <Gift className="h-4 w-4 text-violet-400" />;
    case 'REFUND': return <ArrowUp className="h-4 w-4 text-amber-400" />;
  }
}

function txLabel(reason: string) {
  const labels: Record<string, string> = {
    ocr_sonnet: 'Sonnet OCR (full scoreboard)',
    ocr_haiku: 'Haiku OCR (full scoreboard)',
    ocr_haiku_player: 'Haiku OCR (player stats)',
    pack_STARTER_50: '50 Credit Pack',
    pack_VALUE_200: '200 Credit Pack',
    pack_PRO_500: '500 Credit Pack',
  };
  return labels[reason] || reason;
}

export function OrgCredits() {
  const { orgId } = useParams<{ orgId: string }>();
  const [searchParams] = useSearchParams();
  const justPurchased = searchParams.get('success') === 'true';

  const { data: me } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me });

  const { data: credits, isLoading: creditsLoading } = useQuery({
    queryKey: ['orgCredits', orgId],
    queryFn: () => api.orgCredits(orgId!),
    enabled: !!orgId,
    refetchInterval: justPurchased ? 3000 : false,
  });

  const { data: transactions, isLoading: txLoading } = useQuery({
    queryKey: ['orgCreditTx', orgId],
    queryFn: () => api.orgCreditTransactions(orgId!),
    enabled: !!orgId,
  });

  const [buyError, setBuyError] = useState('');
  const [buyingPack, setBuyingPack] = useState<CreditPack | null>(null);

  const checkoutMut = useMutation({
    mutationFn: ({ pack }: { pack: CreditPack }) => {
      const email = me?.displayName ? `${me.discordId}@gganvil.gg` : '';
      return api.creditCheckout(orgId!, pack, email);
    },
    onSuccess: (result) => {
      window.location.href = result.authorization_url;
    },
    onError: (e: Error) => {
      setBuyError(parseApiError(e).message);
      setBuyingPack(null);
    },
  });

  if (creditsLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  const balance = credits?.balance ?? 0;
  const isNegative = balance < 0;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">OCR Credits</h1>
        <p className="mt-1 text-sm text-gray-400">
          Credits power AI-enhanced scoreboard reading. Free Tesseract extraction is always available.
        </p>
      </div>

      {/* Success banner */}
      {justPurchased && (
        <div className="flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3">
          <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
          <p className="text-sm text-green-300">Payment received! Your credits have been added.</p>
        </div>
      )}

      {/* Balance hero */}
      <Card className="text-center">
        <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Org Balance</p>
        <p className={cn(
          'text-7xl font-black tracking-tight',
          isNegative ? 'text-red-400' : balance === 0 ? 'text-gray-500' : 'text-white'
        )}>
          {balance}
        </p>
        <p className="mt-1 text-sm text-gray-500">credits</p>
        <div className="mt-4 flex justify-center gap-6 text-xs text-gray-500">
          <span>Purchased: <span className="text-gray-300">{credits?.totalPurchased ?? 0}</span></span>
          <span>Used: <span className="text-gray-300">{credits?.totalUsed ?? 0}</span></span>
        </div>
        {isNegative && (
          <p className="mt-3 text-xs text-red-400">Balance is negative — org owner usage is tracked but unlimited.</p>
        )}
      </Card>

      {/* Credit cost guide */}
      <div className="grid grid-cols-3 gap-3 text-center text-xs">
        <div className="rounded-lg border border-gray-700/50 bg-surface-800 p-3">
          <p className="text-2xl font-bold text-violet-400">5</p>
          <p className="mt-1 text-gray-400">Sonnet Full</p>
          <p className="text-gray-600">Leaders / Owners</p>
        </div>
        <div className="rounded-lg border border-gray-700/50 bg-surface-800 p-3">
          <p className="text-2xl font-bold text-amber-400">1</p>
          <p className="mt-1 text-gray-400">Haiku Enhanced</p>
          <p className="text-gray-600">Member opt-in</p>
        </div>
        <div className="rounded-lg border border-gray-700/50 bg-surface-800 p-3">
          <p className="text-2xl font-bold text-green-400">0</p>
          <p className="mt-1 text-gray-400">Tesseract</p>
          <p className="text-gray-600">Always free</p>
        </div>
      </div>

      {/* Purchase packs */}
      <div>
        <h2 className="mb-4 text-lg font-bold text-white">Buy Credits</h2>
        {buyError && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {buyError}
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-3">
          {PACKS.map(pack => {
            const perCredit = (pack.priceZar / pack.credits).toFixed(2);
            const isBuying = buyingPack === pack.key;
            return (
              <Card key={pack.key} className={cn(
                'relative overflow-hidden transition-all hover:border-violet-500/50',
                pack.key === 'VALUE_200' && 'ring-1 ring-violet-500/30'
              )}>
                {pack.key === 'VALUE_200' && (
                  <span className="absolute -top-px right-4 rounded-b-md bg-violet-500 px-2 py-0.5 text-[10px] font-bold text-white uppercase">
                    Popular
                  </span>
                )}
                <div className="flex flex-col items-center text-center">
                  <pack.icon className="mb-3 h-8 w-8 text-violet-400" />
                  <p className="text-lg font-bold text-white">{pack.credits} Credits</p>
                  <p className="text-sm text-gray-400">{pack.label} Pack</p>
                  <p className="mt-3 text-3xl font-black text-white">{formatZar(pack.priceZar)}</p>
                  <p className="text-xs text-gray-500">R{perCredit} per credit</p>
                  <button
                    onClick={() => {
                      setBuyingPack(pack.key);
                      setBuyError('');
                      checkoutMut.mutate({ pack: pack.key });
                    }}
                    disabled={checkoutMut.isPending}
                    className="mt-4 w-full rounded-xl bg-violet-500 px-4 py-2.5 text-sm font-bold text-white transition-all hover:bg-violet-400 disabled:opacity-40"
                  >
                    {isBuying && checkoutMut.isPending ? 'Redirecting...' : 'Buy Now'}
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Transaction history */}
      <div>
        <h2 className="mb-4 text-lg font-bold text-white">Transaction History</h2>
        {txLoading ? (
          <PageSpinner />
        ) : !transactions?.length ? (
          <Card>
            <p className="text-center text-sm text-gray-500">No transactions yet.</p>
          </Card>
        ) : (
          <Card className="divide-y divide-gray-700/30 p-0">
            {transactions.map(tx => (
              <div key={tx.sk} className="flex items-center gap-3 px-4 py-3">
                {txIcon(tx.type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{txLabel(tx.reason)}</p>
                  <p className="text-xs text-gray-500">
                    {tx.consumedBy && `by ${tx.consumedBy} · `}
                    {tx.purchasedBy && `bought by ${tx.purchasedBy} · `}
                    {timeAgo(tx.createdAt)}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className={cn(
                    'text-sm font-bold',
                    tx.amount > 0 ? 'text-green-400' : 'text-red-400'
                  )}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                  </p>
                  <p className="text-xs text-gray-600">{tx.balanceAfter} bal</p>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}
